import { NetworkStatus, useMutation, useQuery } from '@apollo/client'
import { ReactElement, useEffect, useState, createContext, useContext } from 'react'
import styled from 'styled-components'

import { RoutingProjectViewMap } from 'features/RoutingProjects/components/RoutingProjectMap'
import { RoutingProjectSidebar } from 'features/RoutingProjects/components/RoutingProjectSidebar'
import {
  createRoutingProjectPlanModel,
  updateLocationCoordinates,
  updateRoutesPolyline
} from 'features/RoutingProjects/models'
import { GET_ROUTING_PROJECT_PLAN } from 'features/RoutingProjects/queries'
import {
  ILocationCoordinatesUpdate,
  IRoutingProjectModel,
  IRoutingProjectPlan,
  IRoutingProjectPlanModel
} from 'features/RoutingProjects/types'
import { RoutingProjectPlanFragment } from 'gql/generated/graphql'
import { useGoogleMapsScript } from 'hooks/google-maps'
import { useMap } from '@vis.gl/react-google-maps'
import {
  CHANGE_ROUTING_PROJECT_PLAN_DATASET_LOCATIONS,
  CHANGE_ROUTING_PROJECT_PLAN_OPTIMIZATION_ROUTE_STOPS
} from 'features/RoutingProjects/mutations'
import { adjustMapBoundsByRoutes } from 'features/RoutingProjects/utils'
import { isEmpty } from 'lodash'

const RoutingProjectGrid = styled.div<{ isSidebarExpanded: boolean }>`
  height: 100%;
  display: ${({ isSidebarExpanded }): string => (isSidebarExpanded ? 'grid' : 'block')};
  grid-template-columns: 432px minmax(500px, 1fr);
  border-radius: 16px;
  overflow: hidden;
  position: relative;
`

const RoutesPlanningMapContainer = styled.div`
  height: 100%;
`

interface IRoutingProjectPlanContainerProps {
  routingProjectModel: IRoutingProjectModel
}

const PlanContext = createContext<{
  saveUpdatedRoutes: (newPlanModel: IRoutingProjectPlanModel, updatedRoutesIndex: number[]) => Promise<void>
  handleLocationCoordinatesUpdate: ({ locationId, coordinates }: ILocationCoordinatesUpdate) => Promise<void>
} | null>(null)

export interface IPlanContext {
  saveUpdatedRoutes: (newPlanModel: IRoutingProjectPlanModel, updatedRoutesIndex: number[]) => Promise<void>
  handleLocationCoordinatesUpdate: ({ locationId, coordinates }: ILocationCoordinatesUpdate) => Promise<void>
}

export const usePlanContext = (): IPlanContext => {
  const context = useContext(PlanContext)
  if (!context) {
    throw new Error('usePlanContext must be used within a PlanContextProvider')
  }
  return context
}

export const RoutingProjectPlanContainer = ({
  routingProjectModel
}: IRoutingProjectPlanContainerProps): ReactElement => {
  const map = useMap()
  const { geometryLib } = useGoogleMapsScript()
  const [selectedPlan, setSelectedPlan] = useState<IRoutingProjectPlan | null>(null)
  const [visibleRoutesIds, setVisibleRoutesIds] = useState<string[]>([])
  const [planModel, setPlanModel] = useState<IRoutingProjectPlanModel>(createRoutingProjectPlanModel())
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true)
  const {
    data: planData,
    loading: planLoading,
    refetch: planRefetch,
    networkStatus: planNetworkStatus
  } = useQuery(GET_ROUTING_PROJECT_PLAN, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: !selectedPlan?.id,
    variables: {
      input: {
        id: routingProjectModel.id,
        planID: selectedPlan?.id
      }
    }
  })
  const [changeRoutingProjectPlanRouteStops] = useMutation(CHANGE_ROUTING_PROJECT_PLAN_OPTIMIZATION_ROUTE_STOPS, {
    onError() {
      // Do nothing
    }
  })
  const [changeRoutingProjectPlanDatasetLocations] = useMutation(CHANGE_ROUTING_PROJECT_PLAN_DATASET_LOCATIONS, {
    onError() {
      // Do nothing
    }
  })

  useEffect(() => {
    const planModel = createRoutingProjectPlanModel(
      planData?.routingProjectPlan.plan as RoutingProjectPlanFragment,
      geometryLib
    )
    setPlanModel(planModel)
    setVisibleRoutesIds(planModel.optimization.routes.map((route) => route.id))
  }, [planData, geometryLib])

  const saveUpdatedRoutes = async (
    newPlanModel: IRoutingProjectPlanModel,
    updatedRoutesIndex: number[]
  ): Promise<void> => {
    const updatedRoutes = updatedRoutesIndex.map((routeIndex) => {
      return {
        ...newPlanModel.optimization?.routes[routeIndex]
      }
    })
    for (const route of updatedRoutes) {
      await changeRoutingProjectPlanRouteStops({
        variables: {
          input: {
            id: routingProjectModel.id,
            plan: {
              id: newPlanModel.id,
              optimization: {
                route: {
                  id: route.id,
                  polyline: route.polyline,
                  distance: route.distance,
                  duration: route.duration,
                  stops: route.stops.map((stop) => {
                    return {
                      locationID: stop.location.id,
                      departure: stop.departure,
                      arrival: stop.arrival
                    }
                  })
                }
              }
            }
          }
        }
      })
    }
  }

  const handleLocationCoordinatesUpdate = async ({
    locationId,
    coordinates
  }: ILocationCoordinatesUpdate): Promise<void> => {
    const locationLabel = planModel.dataset?.locations?.find((location) => location.id === locationId)?.label || ''
    const newPlan = updateLocationCoordinates(planModel, locationId, coordinates)
    const newPlanModel = {
      ...planModel,
      ...newPlan
    }
    setPlanModel(newPlanModel)
    const routesAffectedIndex = newPlanModel.optimization?.routes.reduce<number[]>((acc, route, index) => {
      const hasLocation = route.stops.some((stop) => stop.location.id === locationId)
      if (hasLocation) {
        acc.push(index)
      }
      return acc
    }, [])
    const updatedPlanModel = await updateRoutesPolyline(newPlanModel, routesAffectedIndex)
    setPlanModel(updatedPlanModel)
    if (map && !isEmpty(routesAffectedIndex)) {
      setTimeout(() => {
        adjustMapBoundsByRoutes({
          map,
          routes: updatedPlanModel.optimization.routes.filter((_, index) => routesAffectedIndex.includes(index))
        })
      }, 1)
    }

    await saveUpdatedRoutes(updatedPlanModel, routesAffectedIndex)

    changeRoutingProjectPlanDatasetLocations({
      variables: {
        input: {
          id: routingProjectModel.id,
          plan: {
            id: planModel.id,
            dataset: {
              locations: [
                {
                  id: locationId,
                  label: locationLabel,
                  coordinates: {
                    latitude: coordinates.latitude.toString(),
                    longitude: coordinates.longitude.toString()
                  }
                }
              ]
            }
          }
        }
      }
    })
  }

  return (
    <PlanContext.Provider value={{ saveUpdatedRoutes, handleLocationCoordinatesUpdate }}>
      <RoutingProjectGrid isSidebarExpanded={isSidebarExpanded}>
        <RoutingProjectSidebar
          routingProjectModel={routingProjectModel}
          planModel={planModel}
          planLoading={planLoading || planNetworkStatus === NetworkStatus.refetch}
          refetchPlan={planRefetch}
          isExpanded={isSidebarExpanded}
          setIsExpanded={setIsSidebarExpanded}
          selectedPlan={selectedPlan}
          setSelectedPlan={setSelectedPlan}
          setPlanModel={setPlanModel}
          visibleRoutesIds={visibleRoutesIds}
          setVisibleRoutesIds={setVisibleRoutesIds}
        />
        <RoutesPlanningMapContainer>
          <RoutingProjectViewMap
            routingProjectId={routingProjectModel.id}
            planModel={planModel}
            planLoading={planLoading || planNetworkStatus === NetworkStatus.refetch}
            visibleRoutesIds={visibleRoutesIds}
          />
        </RoutesPlanningMapContainer>
      </RoutingProjectGrid>
    </PlanContext.Provider>
  )
}
